import React, { TextareaHTMLAttributes, ReactNode, forwardRef } from "react";

/**
* TextArea
* ------------------
* A custom textarea component extending the standard HTML textarea element.
* Add variants for different textarea types.
* 
* Use for multi-line text input fields.
*/

const textAreaVariants = {
  default:
    "border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black resize-y",
  rounded:
    "border border-neutral-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y",
  underlined:
    "border-b border-neutral-300 px-0 py-2 focus:outline-none focus:border-blue-500 resize-y",
  floating:
    "border border-neutral-300 rounded-lg px-3 pt-6 pb-2 focus:outline-none focus:ring-2 focus:ring-black resize-y",
};

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  description?: string;
  icon?: ReactNode;
  variant?: keyof typeof textAreaVariants;
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, description, icon, variant = "default", className, error, ...props }, ref) => {
    const isFloating = variant === "floating";

    const { placeholder, ...rest } = props;

    return (
      <div className="flex flex-col w-full relative">
        <div className="relative flex items-start">
          {isFloating ? (
            <>
              <textarea
                ref={ref}
                placeholder=" " // only to enable :placeholder-shown
                className={`${textAreaVariants[variant]} w-full peer min-h-[80px] ${className || ""} ${
                  error ? "border-red-500 focus:ring-red-500" : ""
                }`}
                {...rest}
              />
              <label
                className={`absolute left-3 top-2 text-neutral-400 text-sm transition-all pointer-events-none
                  peer-placeholder-shown:top-[13px] peer-placeholder-shown:text-base
                  peer-focus:top-2 peer-focus:text-sm peer-focus:text-neutral-600`}
              >
                {label ?? placeholder}
              </label>
            </>
          ) : (
            <div className="w-full">
              {label && (
                <label className="mb-1 text-sm font-medium text-neutral-700">
                  {label}
                </label>
              )}
              <textarea
                ref={ref}
                className={`${textAreaVariants[variant]} w-full min-h-[80px] ${icon ? "pl-10" : ""} ${className || ""} ${
                  error ? "border-red-500 focus:ring-red-500" : ""
                }`}
                placeholder={placeholder}
                {...rest}
              />
            </div>
          )}

          {icon && !isFloating && <span className="absolute left-3 top-3">{icon}</span>}
        </div>

        {description && !error && <small className="text-xs text-gray-500">{description}</small>}
        {error && <small className="mt-1 text-xs text-red-500">{error}</small>}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
